import React, { useEffect, useState } from 'react';
import { FormTexts } from '../../../language/Texts';
import { ComponentType } from '../../../models/componentType.enum';
import './ColorPicker.scss';

interface ColorPickerProps {
    manufacturerColor: string;
    setManufacturerColor: (color: string) => void;
    type?: ComponentType;
}

function expandShortHex(shortHex: string) {
    // "#abc" -> "#aabbcc"
    const h = shortHex.replace('#', '');
    if (h.length === 3) {
        return (
            '#' +
            h
                .split('')
                .map(ch => ch + ch)
                .join('')
        );
    }
    return '#' + h;
}

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
    if (!hex) return null;
    let h = hex.replace('#', '');
    if (h.length === 3) h = h.split('').map(c => c + c).join('');
    if (h.length !== 6) return null;
    const r = parseInt(h.substring(0, 2), 16);
    const g = parseInt(h.substring(2, 4), 16);
    const b = parseInt(h.substring(4, 6), 16);
    if (Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)) return null;
    return { r, g, b };
}

function rgbToHex(r: number, g: number, b: number) {
    const toHex = (n: number) => n.toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function parseRgbString(s: string): { r: number; g: number; b: number; a: number } | null {
    // matches "rgb(r,g,b)" and "rgba(r,g,b,a)"
    const m = s.match(/rgba?\s*\(\s*([0-9]+)\s*,\s*([0-9]+)\s*,\s*([0-9]+)(?:\s*,\s*([0-9.]+))?\s*\)/i);
    if (!m) return null;
    const r = parseInt(m[1], 10);
    const g = parseInt(m[2], 10);
    const b = parseInt(m[3], 10);
    const a = m[4] !== undefined ? parseFloat(m[4]) : 1;
    return { r, g, b, a };
}

function ColorPicker({ manufacturerColor, setManufacturerColor, type }: ColorPickerProps) {
    // local states: hex color and opacity
    const [hexColor, setHexColor] = useState<string>('#000000');
    const [opacity, setOpacity] = useState<number>(0.3);

    // initialize local state from prop on mount and when prop changes
    useEffect(() => {
        if (!manufacturerColor) {
            updateParent(hexColor, opacity);
            return;
        }
        const trimmed = manufacturerColor.trim();
        if (trimmed.startsWith('#')) {
            const hx = expandShortHex(trimmed);
            setHexColor(hx);
            setOpacity(1);
            // inform parent in a normalized form (rgba with alpha=1)
            const rgb = hexToRgb(hx);
            if (rgb) setManufacturerColor(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 1)`);
        } else if (trimmed.toLowerCase().startsWith('rgb')) {
            const parsed = parseRgbString(trimmed);
            if (parsed) {
                setHexColor(rgbToHex(parsed.r, parsed.g, parsed.b));
                setOpacity(isFinite(parsed.a) ? parsed.a : 1);
            }
        } else {
            // unknown format - try to be conservative: keep as black
            setHexColor('#161f2859');
            setOpacity(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [manufacturerColor]);

    const updateParent = (hex: string, newOpacity: number) => {
        const rgb = hexToRgb(hex);
        if (!rgb) return;
        setManufacturerColor(`rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${Number(newOpacity.toFixed(3))})`);
    };

    const handleColorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newHex = e.target.value;
        setHexColor(newHex);
        updateParent(newHex, opacity);
    };

    const handleOpacityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const v = parseFloat(e.target.value);
        // clamp between 0 and 1
        const clamped = Math.min(1, Math.max(0, isNaN(v) ? 1 : v));
        setOpacity(clamped);
        updateParent(hexColor, clamped);
    };

    const displayValue = opacity >= 0.999 ? hexColor : (() => {
        const rgb = hexToRgb(hexColor);
        if (!rgb) return hexColor;
        return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${opacity})`;
    })();

    return (
        <div className={`input_pair color_picker_wrapper ${type === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_pair' : ''}`}

        >
            <label className={`input_label ${type === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_label' : ''}`}>{FormTexts.manufacturerColor}</label>
            <div className="color_wrapper">
                <input
                    data-testid="color_input"
                    className="color_input"
                    type="color"
                    value={hexColor}
                    onChange={handleColorChange}
                />
                <span className="color_value">{displayValue}</span>
                <input
                    className={`opacity_slider ${type === ComponentType.GEMUE_EXTERNAL ? 'gemue_opacity_slider' : ''}`}
                    type="range"
                    title="Opacity"
                    name="opacity"
                    value={String(opacity)}
                    min={0}
                    max={1}
                    step={0.01}
                    onChange={handleOpacityChange}
                />
            </div>
        </div>
    );
}

export default ColorPicker;
